/**
 * Detailed anatomical body diagram (anterior + posterior).
 * ~50 named muscle groups grouped by region for therapeutic precision.
 */
import React from "react";

export const MUSCLE_LABELS = {
  // Face
  frontalis: "Frontalis",
  temporalis: "Temporalis",
  orbicularis_oculi: "Orbicularis Oculi",
  masseter: "Masseter",
  zygomaticus: "Zygomaticus",
  orbicularis_oris: "Orbicularis Oris",
  mentalis: "Mentalis",
  occipitalis: "Occipitalis",
  // Anterior - Head & Neck
  scm: "Sternocleidomastoid",
  scalenes: "Scalenes",
  platysma: "Platysma",
  digastric: "Digastric",
  suprahyoids: "Suprahyoids",
  infrahyoids: "Infrahyoids",
  longus_colli: "Longus Colli / Capitis",
  // Posterior - Deep Neck
  suboccipitals: "Suboccipitals",
  splenius_capitis: "Splenius Capitis",
  splenius_cervicis: "Splenius Cervicis",
  semispinalis_capitis: "Semispinalis Capitis",
  semispinalis_cervicis: "Semispinalis Cervicis",
  multifidus_cervicis: "Multifidus Cervicis",
  // Anterior - Shoulders
  anterior_deltoid: "Anterior Deltoid",
  lateral_deltoid: "Lateral Deltoid",
  // Anterior - Chest
  pec_major_clavicular: "Pec Major (Clavicular)",
  pec_major_sternal: "Pec Major (Sternal)",
  pec_minor: "Pec Minor",
  serratus_anterior: "Serratus Anterior",
  // Anterior - Arms
  biceps_brachii: "Biceps Brachii",
  brachialis: "Brachialis",
  brachioradialis: "Brachioradialis",
  wrist_flexors: "Wrist Flexors",
  // Anterior - Core
  rectus_abdominis_upper: "Rectus Abdominis (Upper)",
  rectus_abdominis_lower: "Rectus Abdominis (Lower)",
  external_obliques: "External Obliques",
  // Anterior - Hip & Thigh
  psoas_iliacus: "Psoas / Iliacus",
  tfl: "Tensor Fasciae Latae",
  sartorius: "Sartorius",
  rectus_femoris: "Rectus Femoris",
  vastus_lateralis: "Vastus Lateralis",
  vastus_medialis: "Vastus Medialis",
  adductor_longus: "Adductor Longus",
  adductor_magnus: "Adductor Magnus",
  gracilis: "Gracilis",
  // Anterior - Lower leg
  tibialis_anterior: "Tibialis Anterior",
  peroneals: "Peroneals",
  // Posterior - Upper back / shoulders
  upper_trapezius: "Upper Trapezius",
  middle_trapezius: "Middle Trapezius",
  lower_trapezius: "Lower Trapezius",
  rhomboids: "Rhomboids",
  levator_scapulae: "Levator Scapulae",
  posterior_deltoid: "Posterior Deltoid",
  supraspinatus: "Supraspinatus",
  infraspinatus: "Infraspinatus",
  teres_minor: "Teres Minor",
  teres_major: "Teres Major",
  // Posterior - Back
  lats_upper: "Lats (Upper)",
  lats_lower: "Lats (Lower)",
  erector_spinae: "Erector Spinae",
  quadratus_lumborum: "Quadratus Lumborum",
  thoracolumbar_fascia: "Thoracolumbar Fascia",
  // Posterior - Arms
  triceps_long: "Triceps (Long Head)",
  triceps_lateral: "Triceps (Lateral Head)",
  wrist_extensors: "Wrist Extensors",
  // Posterior - Glutes
  gluteus_maximus: "Gluteus Maximus",
  gluteus_medius: "Gluteus Medius",
  piriformis: "Piriformis",
  // Posterior - Hamstrings
  biceps_femoris: "Biceps Femoris",
  semitendinosus: "Semitendinosus",
  semimembranosus: "Semimembranosus",
  // Posterior - Lateral leg
  IT_band: "IT Band",
  // Posterior - Calves
  gastrocnemius_medial: "Gastrocnemius (Medial)",
  gastrocnemius_lateral: "Gastrocnemius (Lateral)",
  soleus: "Soleus",
};

const BODY_OUTLINE = "M120 22 c16 0 26 12 26 28 c0 10 -3 18 -9 26 c10 4 22 14 26 28 l10 32 c4 12 0 18 -8 20 c-6 2 -10 0 -12 -8 l-6 -16 v50 c0 24 -2 42 -4 60 l-2 32 c10 6 14 16 16 32 l10 92 c2 20 -2 32 -12 34 c-10 2 -14 -4 -16 -18 l-12 -68 l-2 80 c0 16 -4 24 -12 24 h-6 c-8 0 -12 -8 -12 -24 l-2 -80 l-12 68 c-2 14 -6 20 -16 18 c-10 -2 -14 -14 -12 -34 l10 -92 c2 -16 6 -26 16 -32 l-2 -32 c-2 -18 -4 -36 -4 -60 v-50 l-6 16 c-2 8 -6 10 -12 8 c-8 -2 -12 -8 -8 -20 l10 -32 c4 -14 16 -24 26 -28 c-6 -8 -9 -16 -9 -26 c0 -16 10 -28 26 -28 Z";

const Region = ({ id, selected, aiFocus, hoveredId, primaryFocus = [], secondaryFocus = [], relatedFocus = [], heatmap = null, onToggle, onHover, onLongPress, children, ...attrs }) => {
  const isSel = selected.includes(id);
  const isAi = aiFocus.includes(id);
  const isHov = hoveredId === id;
  const isPrimary = primaryFocus.includes(id);
  const isSecondary = secondaryFocus.includes(id);
  const isRelated = relatedFocus.includes(id);
  const priorityCls = isPrimary ? "primary-focus" : isSecondary ? "secondary-focus" : isRelated ? "related-focus" : "";
  // Heatmap state — when supplied, drives the fill color/intensity via CSS vars.
  const heat = heatmap ? heatmap[id] : null;
  const heatCls = heat
    ? `heatmap-on heatmap-${heat.trend || "stable"}${heat.count >= 3 ? " heatmap-recurring" : ""}`
    : "";
  const heatStyle = heat ? { "--heat-intensity": Math.max(0.18, Math.min(1, heat.intensity)) } : undefined;
  const cls = `muscle-path ${isSel ? "selected" : ""} ${isAi ? "ai-focus" : ""} ${isHov ? "hovered" : ""} ${priorityCls} ${heatCls}`.trim();
  // Long-press → fire onLongPress(id). Pointer-events work for mouse + touch + pen.
  const pressTimerRef = React.useRef(null);
  const longPressedRef = React.useRef(false);
  const startPress = () => {
    longPressedRef.current = false;
    pressTimerRef.current = setTimeout(() => {
      longPressedRef.current = true;
      onLongPress?.(id);
    }, 550);
  };
  const endPress = () => {
    clearTimeout(pressTimerRef.current);
  };
  const handleClick = (e) => {
    if (longPressedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onToggle(id);
  };
  const heatTitle = heat
    ? ` · worked ${heat.count}x${heat.trend && heat.trend !== "unknown" ? ` · ${heat.trend}` : ""}`
    : "";
  return (
    <g
      onClick={handleClick}
      onMouseEnter={() => onHover?.(id)}
      onMouseLeave={() => onHover?.(null)}
      onPointerDown={startPress}
      onPointerUp={endPress}
      onPointerLeave={endPress}
      onTouchStart={() => onHover?.(id)}
      data-testid={`muscle-${id}`}
      data-heat-count={heat?.count}
      data-heat-trend={heat?.trend}
      style={{ cursor: "pointer" }}
    >
      {React.Children.map(children, (child) => React.cloneElement(child, { className: cls, style: heatStyle, ...attrs }))}
      <title>{MUSCLE_LABELS[id]}{isAi ? " — AI focus" : ""}{isPrimary ? " — Primary focus" : ""}{isSecondary ? " — Secondary focus" : ""}{isRelated ? " — Related" : ""}{isSel ? " — selected" : ""}{heatTitle} · hold to capture</title>
    </g>
  );
};

// ============================================================
// HEAD / FACE DETAIL (ANTERIOR) — zoomed view
// ============================================================
function HeadFront({ selected, aiFocus, hoveredId, onToggle, onHover, onLongPress, primaryFocus, secondaryFocus, relatedFocus, heatmap }) {
  const props = { selected, aiFocus, hoveredId, onToggle, onHover, onLongPress, primaryFocus, secondaryFocus, relatedFocus, heatmap };
  return (
    <svg viewBox="0 0 200 200" className="w-full h-auto select-none" data-testid="head-detail-front">
      {/* Face/head outline */}
      <ellipse cx="100" cy="84" rx="50" ry="62" fill="#FDFCFB" stroke="#C5BFB4" strokeWidth="1.2" />
      {/* Neck outline */}
      <rect x="78" y="142" width="44" height="46" rx="6" fill="#FDFCFB" stroke="#C5BFB4" strokeWidth="1.2" />

      {/* Frontalis (forehead) */}
      <Region id="frontalis" {...props}>
        <path d="M64 50 q36 -16 72 0 q-4 14 -36 14 q-32 0 -36 -14 Z" />
      </Region>
      {/* Temporalis - both temples */}
      <Region id="temporalis" {...props}>
        <ellipse cx="58" cy="70" rx="8" ry="14" />
      </Region>
      <Region id="temporalis" {...props}>
        <ellipse cx="142" cy="70" rx="8" ry="14" />
      </Region>
      {/* Orbicularis oculi - around eyes */}
      <Region id="orbicularis_oculi" {...props}>
        <ellipse cx="82" cy="78" rx="11" ry="6" />
      </Region>
      <Region id="orbicularis_oculi" {...props}>
        <ellipse cx="118" cy="78" rx="11" ry="6" />
      </Region>
      {/* Zygomaticus - cheek */}
      <Region id="zygomaticus" {...props}>
        <path d="M78 92 q-4 14 6 22 q6 -2 6 -10 q-2 -10 -12 -12 Z" />
      </Region>
      <Region id="zygomaticus" {...props}>
        <path d="M122 92 q4 14 -6 22 q-6 -2 -6 -10 q2 -10 12 -12 Z" />
      </Region>
      {/* Masseter - jaw */}
      <Region id="masseter" {...props}>
        <ellipse cx="68" cy="118" rx="9" ry="14" />
      </Region>
      <Region id="masseter" {...props}>
        <ellipse cx="132" cy="118" rx="9" ry="14" />
      </Region>
      {/* Orbicularis oris - around mouth */}
      <Region id="orbicularis_oris" {...props}>
        <ellipse cx="100" cy="116" rx="14" ry="5" />
      </Region>
      {/* Mentalis - chin */}
      <Region id="mentalis" {...props}>
        <ellipse cx="100" cy="134" rx="9" ry="5" />
      </Region>

      {/* Neck region (zoomed) */}
      {/* SCM - both sides, prominent diagonal */}
      <Region id="scm" {...props}>
        <path d="M82 146 q-4 18 -4 38 q5 2 9 -2 q2 -20 4 -36 Z" />
      </Region>
      <Region id="scm" {...props}>
        <path d="M118 146 q4 18 4 38 q-5 2 -9 -2 q-2 -20 -4 -36 Z" />
      </Region>
      {/* Digastric - under jaw */}
      <Region id="digastric" {...props}>
        <path d="M86 148 q14 -4 28 0 q-2 6 -14 6 q-12 0 -14 -6 Z" />
      </Region>
      {/* Suprahyoids (upper anterior neck) */}
      <Region id="suprahyoids" {...props}>
        <ellipse cx="100" cy="158" rx="9" ry="4" />
      </Region>
      {/* Infrahyoids (lower anterior neck) */}
      <Region id="infrahyoids" {...props}>
        <rect x="92" y="164" width="16" height="14" rx="2" />
      </Region>
      {/* Scalenes - lateral to infrahyoids */}
      <Region id="scalenes" {...props}>
        <ellipse cx="88" cy="174" rx="5" ry="9" />
      </Region>
      <Region id="scalenes" {...props}>
        <ellipse cx="112" cy="174" rx="5" ry="9" />
      </Region>
      {/* Longus colli - deep neck flexors (central) */}
      <Region id="longus_colli" {...props}>
        <ellipse cx="100" cy="180" rx="4" ry="6" />
      </Region>
      {/* Platysma - superficial broad anterior neck */}
      <Region id="platysma" {...props}>
        <path d="M78 144 q44 -4 44 0 l-2 6 q-20 -2 -40 0 Z" />
      </Region>
    </svg>
  );
}

// ============================================================
// HEAD / NECK DETAIL (POSTERIOR) — zoomed view
// ============================================================
function HeadBack({ selected, aiFocus, hoveredId, onToggle, onHover, onLongPress, primaryFocus, secondaryFocus, relatedFocus, heatmap }) {
  const props = { selected, aiFocus, hoveredId, onToggle, onHover, onLongPress, primaryFocus, secondaryFocus, relatedFocus, heatmap };
  return (
    <svg viewBox="0 0 200 200" className="w-full h-auto select-none" data-testid="head-detail-back">
      {/* Cranium outline */}
      <ellipse cx="100" cy="78" rx="52" ry="58" fill="#FDFCFB" stroke="#C5BFB4" strokeWidth="1.2" />
      {/* Neck outline */}
      <rect x="76" y="134" width="48" height="54" rx="6" fill="#FDFCFB" stroke="#C5BFB4" strokeWidth="1.2" />

      {/* Occipitalis - back of head */}
      <Region id="occipitalis" {...props}>
        <path d="M58 96 q42 -10 84 0 q-4 22 -42 22 q-38 0 -42 -22 Z" />
      </Region>
      {/* Temporalis - posterior temples */}
      <Region id="temporalis" {...props}>
        <ellipse cx="56" cy="76" rx="8" ry="14" />
      </Region>
      <Region id="temporalis" {...props}>
        <ellipse cx="144" cy="76" rx="8" ry="14" />
      </Region>

      {/* Suboccipitals - just below skull */}
      <Region id="suboccipitals" {...props}>
        <ellipse cx="100" cy="130" rx="14" ry="4" />
      </Region>

      {/* Splenius capitis - upper lateral neck */}
      <Region id="splenius_capitis" {...props}>
        <path d="M82 138 q-2 12 6 22 q5 -2 5 -10 q-2 -10 -11 -12 Z" />
      </Region>
      <Region id="splenius_capitis" {...props}>
        <path d="M118 138 q2 12 -6 22 q-5 -2 -5 -10 q2 -10 11 -12 Z" />
      </Region>

      {/* Splenius cervicis - mid lateral neck */}
      <Region id="splenius_cervicis" {...props}>
        <ellipse cx="84" cy="166" rx="6" ry="10" />
      </Region>
      <Region id="splenius_cervicis" {...props}>
        <ellipse cx="116" cy="166" rx="6" ry="10" />
      </Region>

      {/* Semispinalis capitis - paramedian */}
      <Region id="semispinalis_capitis" {...props}>
        <ellipse cx="92" cy="146" rx="5" ry="10" />
      </Region>
      <Region id="semispinalis_capitis" {...props}>
        <ellipse cx="108" cy="146" rx="5" ry="10" />
      </Region>

      {/* Semispinalis cervicis - lower paramedian */}
      <Region id="semispinalis_cervicis" {...props}>
        <ellipse cx="94" cy="170" rx="4" ry="10" />
      </Region>
      <Region id="semispinalis_cervicis" {...props}>
        <ellipse cx="106" cy="170" rx="4" ry="10" />
      </Region>

      {/* Multifidus cervicis - deepest central */}
      <Region id="multifidus_cervicis" {...props}>
        <rect x="98" y="156" width="4" height="28" rx="2" />
      </Region>

      {/* Upper trapezius - lateral wedges */}
      <Region id="upper_trapezius" {...props}>
        <path d="M76 178 q12 -2 22 -2 v8 q-14 0 -22 -6 Z" />
      </Region>
      <Region id="upper_trapezius" {...props}>
        <path d="M124 178 q-12 -2 -22 -2 v8 q14 0 22 -6 Z" />
      </Region>

      {/* Levator scapulae - lateral lower */}
      <Region id="levator_scapulae" {...props}>
        <ellipse cx="78" cy="174" rx="3" ry="8" />
      </Region>
      <Region id="levator_scapulae" {...props}>
        <ellipse cx="122" cy="174" rx="3" ry="8" />
      </Region>
    </svg>
  );
}

// ============================================================
// ANTERIOR (FRONT) — full body
// ============================================================
function FrontFigure({ selected, aiFocus, hoveredId, onToggle, onHover, onLongPress, primaryFocus, secondaryFocus, relatedFocus, heatmap }) {
  const props = { selected, aiFocus, hoveredId, onToggle, onHover, onLongPress, primaryFocus, secondaryFocus, relatedFocus, heatmap };
  return (
    <svg viewBox="0 0 240 600" className="w-full h-auto select-none" data-testid="body-diagram-front">
      <path d={BODY_OUTLINE} fill="#FDFCFB" stroke="#C5BFB4" strokeWidth="1.2" />

      {/* Neck - SCM */}
      <Region id="scm" {...props}>
        <path d="M108 64 q-4 12 -2 22 q4 2 6 -2 q2 -10 0 -22 Z" />
      </Region>
      <Region id="scm" {...props}>
        <path d="M132 64 q4 12 2 22 q-4 2 -6 -2 q-2 -10 0 -22 Z" />
      </Region>
      <Region id="scalenes" {...props}>
        <ellipse cx="120" cy="74" rx="6" ry="9" />
      </Region>
      <Region id="platysma" {...props}>
        <path d="M104 84 q16 -4 32 0 q-2 6 -16 6 q-14 0 -16 -6 Z" />
      </Region>

      {/* Shoulders */}
      <Region id="anterior_deltoid" {...props}>
        <path d="M82 96 q-12 6 -16 22 q14 2 22 -6 q2 -10 -6 -16 Z" />
      </Region>
      <Region id="anterior_deltoid" {...props}>
        <path d="M158 96 q12 6 16 22 q-14 2 -22 -6 q-2 -10 6 -16 Z" />
      </Region>
      <Region id="lateral_deltoid" {...props}>
        <ellipse cx="64" cy="118" rx="10" ry="14" />
      </Region>
      <Region id="lateral_deltoid" {...props}>
        <ellipse cx="176" cy="118" rx="10" ry="14" />
      </Region>

      {/* Chest */}
      <Region id="pec_major_clavicular" {...props}>
        <path d="M88 100 q32 -6 64 0 q-2 14 -32 14 q-30 0 -32 -14 Z" />
      </Region>
      <Region id="pec_major_sternal" {...props}>
        <path d="M88 116 q32 -2 64 0 q-2 18 -32 22 q-30 -4 -32 -22 Z" />
      </Region>
      <Region id="pec_minor" {...props}>
        <ellipse cx="100" cy="132" rx="9" ry="5" />
      </Region>
      <Region id="pec_minor" {...props}>
        <ellipse cx="140" cy="132" rx="9" ry="5" />
      </Region>
      <Region id="serratus_anterior" {...props}>
        <path d="M82 140 q-2 18 4 36 q8 -2 10 -8 q-2 -14 -6 -28 Z" />
      </Region>
      <Region id="serratus_anterior" {...props}>
        <path d="M158 140 q2 18 -4 36 q-8 -2 -10 -8 q2 -14 6 -28 Z" />
      </Region>

      {/* Arms */}
      <Region id="biceps_brachii" {...props}>
        <ellipse cx="56" cy="158" rx="9" ry="22" />
      </Region>
      <Region id="biceps_brachii" {...props}>
        <ellipse cx="184" cy="158" rx="9" ry="22" />
      </Region>
      <Region id="brachialis" {...props}>
        <ellipse cx="52" cy="190" rx="7" ry="10" />
      </Region>
      <Region id="brachialis" {...props}>
        <ellipse cx="188" cy="190" rx="7" ry="10" />
      </Region>
      <Region id="brachioradialis" {...props}>
        <path d="M44 204 q-2 14 0 26 q8 -2 8 -14 q0 -10 -2 -14 Z" />
      </Region>
      <Region id="brachioradialis" {...props}>
        <path d="M196 204 q2 14 0 26 q-8 -2 -8 -14 q0 -10 2 -14 Z" />
      </Region>
      <Region id="wrist_flexors" {...props}>
        <ellipse cx="44" cy="234" rx="7" ry="16" />
      </Region>
      <Region id="wrist_flexors" {...props}>
        <ellipse cx="196" cy="234" rx="7" ry="16" />
      </Region>

      {/* Core */}
      <Region id="rectus_abdominis_upper" {...props}>
        <rect x="108" y="146" width="24" height="32" rx="3" />
      </Region>
      <Region id="rectus_abdominis_lower" {...props}>
        <rect x="108" y="180" width="24" height="32" rx="3" />
      </Region>
      <Region id="external_obliques" {...props}>
        <path d="M92 150 q-4 30 4 62 q-14 -2 -18 -28 Z" />
      </Region>
      <Region id="external_obliques" {...props}>
        <path d="M148 150 q4 30 -4 62 q14 -2 18 -28 Z" />
      </Region>

      {/* Hip & Thigh */}
      <Region id="psoas_iliacus" {...props}>
        <ellipse cx="104" cy="226" rx="7" ry="10" />
      </Region>
      <Region id="psoas_iliacus" {...props}>
        <ellipse cx="136" cy="226" rx="7" ry="10" />
      </Region>
      <Region id="tfl" {...props}>
        <ellipse cx="86" cy="232" rx="6" ry="10" />
      </Region>
      <Region id="tfl" {...props}>
        <ellipse cx="154" cy="232" rx="6" ry="10" />
      </Region>
      <Region id="sartorius" {...props}>
        <path d="M100 244 q-4 40 -16 78 q-2 -2 0 -6 q12 -36 12 -72 Z" />
      </Region>
      <Region id="sartorius" {...props}>
        <path d="M140 244 q4 40 16 78 q2 -2 0 -6 q-12 -36 -12 -72 Z" />
      </Region>
      <Region id="rectus_femoris" {...props}>
        <ellipse cx="100" cy="290" rx="9" ry="34" />
      </Region>
      <Region id="rectus_femoris" {...props}>
        <ellipse cx="140" cy="290" rx="9" ry="34" />
      </Region>
      <Region id="vastus_lateralis" {...props}>
        <ellipse cx="82" cy="296" rx="9" ry="32" />
      </Region>
      <Region id="vastus_lateralis" {...props}>
        <ellipse cx="158" cy="296" rx="9" ry="32" />
      </Region>
      <Region id="vastus_medialis" {...props}>
        <ellipse cx="112" cy="324" rx="8" ry="14" />
      </Region>
      <Region id="vastus_medialis" {...props}>
        <ellipse cx="128" cy="324" rx="8" ry="14" />
      </Region>
      <Region id="adductor_longus" {...props}>
        <path d="M114 252 q-4 32 -2 54 q-8 -28 -4 -54 Z" />
      </Region>
      <Region id="adductor_longus" {...props}>
        <path d="M126 252 q4 32 2 54 q8 -28 4 -54 Z" />
      </Region>
      <Region id="adductor_magnus" {...props}>
        <path d="M118 268 q-2 30 0 56 q-4 -28 -2 -56 Z" />
      </Region>
      <Region id="adductor_magnus" {...props}>
        <path d="M122 268 q2 30 0 56 q4 -28 2 -56 Z" />
      </Region>
      <Region id="gracilis" {...props}>
        <path d="M116 260 q-2 38 -4 70 q-2 -2 0 -6 q2 -32 2 -64 Z" />
      </Region>
      <Region id="gracilis" {...props}>
        <path d="M124 260 q2 38 4 70 q2 -2 0 -6 q-2 -32 -2 -64 Z" />
      </Region>

      {/* Lower leg */}
      <Region id="tibialis_anterior" {...props}>
        <ellipse cx="102" cy="420" rx="8" ry="40" />
      </Region>
      <Region id="tibialis_anterior" {...props}>
        <ellipse cx="138" cy="420" rx="8" ry="40" />
      </Region>
      <Region id="peroneals" {...props}>
        <path d="M88 408 q-2 30 0 50 q6 -2 6 -16 q0 -20 -2 -34 Z" />
      </Region>
      <Region id="peroneals" {...props}>
        <path d="M152 408 q2 30 0 50 q-6 -2 -6 -16 q0 -20 2 -34 Z" />
      </Region>
    </svg>
  );
}

// ============================================================
// POSTERIOR (BACK)
// ============================================================
function BackFigure({ selected, aiFocus, hoveredId, onToggle, onHover, onLongPress, primaryFocus, secondaryFocus, relatedFocus, heatmap }) {
  const props = { selected, aiFocus, hoveredId, onToggle, onHover, onLongPress, primaryFocus, secondaryFocus, relatedFocus, heatmap };
  return (
    <svg viewBox="0 0 240 600" className="w-full h-auto select-none" data-testid="body-diagram-back">
      <path d={BODY_OUTLINE} fill="#FDFCFB" stroke="#C5BFB4" strokeWidth="1.2" />

      {/* Posterior neck */}
      <Region id="suboccipitals" {...props}>
        <ellipse cx="120" cy="52" rx="10" ry="4" />
      </Region>
      <Region id="splenius_capitis" {...props}>
        <path d="M108 60 q12 -4 24 0 q-2 8 -12 8 q-10 0 -12 -8 Z" />
      </Region>

      {/* Upper trapezius */}
      <Region id="upper_trapezius" {...props}>
        <path d="M86 76 q34 -10 68 0 q-4 16 -34 16 q-30 0 -34 -16 Z" />
      </Region>

      {/* Levator scapulae */}
      <Region id="levator_scapulae" {...props}>
        <ellipse cx="92" cy="90" rx="5" ry="9" />
      </Region>
      <Region id="levator_scapulae" {...props}>
        <ellipse cx="148" cy="90" rx="5" ry="9" />
      </Region>

      {/* Rhomboids */}
      <Region id="rhomboids" {...props}>
        <path d="M94 102 q26 -3 52 0 l-5 26 q-21 -3 -42 0 Z" />
      </Region>

      {/* Middle trapezius */}
      <Region id="middle_trapezius" {...props}>
        <path d="M86 100 q-2 16 4 30 q12 -2 12 -16 q-2 -10 -16 -14 Z" />
      </Region>
      <Region id="middle_trapezius" {...props}>
        <path d="M154 100 q2 16 -4 30 q-12 -2 -12 -16 q2 -10 16 -14 Z" />
      </Region>

      {/* Lower trapezius */}
      <Region id="lower_trapezius" {...props}>
        <path d="M104 130 q16 -2 32 0 l-4 30 q-12 -2 -24 0 Z" />
      </Region>

      {/* Posterior deltoid */}
      <Region id="posterior_deltoid" {...props}>
        <ellipse cx="68" cy="110" rx="11" ry="14" />
      </Region>
      <Region id="posterior_deltoid" {...props}>
        <ellipse cx="172" cy="110" rx="11" ry="14" />
      </Region>

      {/* Rotator cuff - supraspinatus / infraspinatus / teres */}
      <Region id="supraspinatus" {...props}>
        <ellipse cx="84" cy="98" rx="5" ry="3" />
      </Region>
      <Region id="supraspinatus" {...props}>
        <ellipse cx="156" cy="98" rx="5" ry="3" />
      </Region>
      <Region id="infraspinatus" {...props}>
        <ellipse cx="80" cy="118" rx="8" ry="9" />
      </Region>
      <Region id="infraspinatus" {...props}>
        <ellipse cx="160" cy="118" rx="8" ry="9" />
      </Region>
      <Region id="teres_minor" {...props}>
        <ellipse cx="84" cy="132" rx="6" ry="5" />
      </Region>
      <Region id="teres_minor" {...props}>
        <ellipse cx="156" cy="132" rx="6" ry="5" />
      </Region>
      <Region id="teres_major" {...props}>
        <ellipse cx="84" cy="144" rx="7" ry="7" />
      </Region>
      <Region id="teres_major" {...props}>
        <ellipse cx="156" cy="144" rx="7" ry="7" />
      </Region>

      {/* Lats */}
      <Region id="lats_upper" {...props}>
        <path d="M76 150 q-4 18 4 32 q14 -2 22 -10 v-22 q-12 0 -26 0 Z" />
      </Region>
      <Region id="lats_upper" {...props}>
        <path d="M164 150 q4 18 -4 32 q-14 -2 -22 -10 v-22 q12 0 26 0 Z" />
      </Region>
      <Region id="lats_lower" {...props}>
        <path d="M82 182 q-4 14 0 26 q14 -2 20 -8 v-22 q-12 0 -20 4 Z" />
      </Region>
      <Region id="lats_lower" {...props}>
        <path d="M158 182 q4 14 0 26 q-14 -2 -20 -8 v-22 q12 0 20 4 Z" />
      </Region>

      {/* Erector spinae */}
      <Region id="erector_spinae" {...props}>
        <rect x="110" y="148" width="8" height="58" rx="3" />
      </Region>
      <Region id="erector_spinae" {...props}>
        <rect x="122" y="148" width="8" height="58" rx="3" />
      </Region>
      <Region id="quadratus_lumborum" {...props}>
        <ellipse cx="104" cy="216" rx="8" ry="8" />
      </Region>
      <Region id="quadratus_lumborum" {...props}>
        <ellipse cx="136" cy="216" rx="8" ry="8" />
      </Region>
      <Region id="thoracolumbar_fascia" {...props}>
        <rect x="114" y="206" width="12" height="22" rx="2" />
      </Region>

      {/* Triceps */}
      <Region id="triceps_long" {...props}>
        <ellipse cx="58" cy="160" rx="8" ry="22" />
      </Region>
      <Region id="triceps_long" {...props}>
        <ellipse cx="182" cy="160" rx="8" ry="22" />
      </Region>
      <Region id="triceps_lateral" {...props}>
        <ellipse cx="46" cy="166" rx="6" ry="18" />
      </Region>
      <Region id="triceps_lateral" {...props}>
        <ellipse cx="194" cy="166" rx="6" ry="18" />
      </Region>
      <Region id="wrist_extensors" {...props}>
        <ellipse cx="44" cy="222" rx="7" ry="22" />
      </Region>
      <Region id="wrist_extensors" {...props}>
        <ellipse cx="196" cy="222" rx="7" ry="22" />
      </Region>

      {/* Glutes */}
      <Region id="gluteus_maximus" {...props}>
        <ellipse cx="102" cy="244" rx="16" ry="16" />
      </Region>
      <Region id="gluteus_maximus" {...props}>
        <ellipse cx="138" cy="244" rx="16" ry="16" />
      </Region>
      <Region id="gluteus_medius" {...props}>
        <ellipse cx="86" cy="234" rx="8" ry="9" />
      </Region>
      <Region id="gluteus_medius" {...props}>
        <ellipse cx="154" cy="234" rx="8" ry="9" />
      </Region>
      <Region id="piriformis" {...props}>
        <ellipse cx="106" cy="238" rx="6" ry="3" />
      </Region>
      <Region id="piriformis" {...props}>
        <ellipse cx="134" cy="238" rx="6" ry="3" />
      </Region>

      {/* Hamstrings */}
      <Region id="biceps_femoris" {...props}>
        <ellipse cx="86" cy="300" rx="9" ry="34" />
      </Region>
      <Region id="biceps_femoris" {...props}>
        <ellipse cx="154" cy="300" rx="9" ry="34" />
      </Region>
      <Region id="semitendinosus" {...props}>
        <ellipse cx="102" cy="306" rx="8" ry="34" />
      </Region>
      <Region id="semitendinosus" {...props}>
        <ellipse cx="138" cy="306" rx="8" ry="34" />
      </Region>
      <Region id="semimembranosus" {...props}>
        <ellipse cx="116" cy="312" rx="6" ry="30" />
      </Region>
      <Region id="semimembranosus" {...props}>
        <ellipse cx="124" cy="312" rx="6" ry="30" />
      </Region>

      {/* IT band */}
      <Region id="IT_band" {...props}>
        <rect x="68" y="270" width="4" height="78" rx="2" />
      </Region>
      <Region id="IT_band" {...props}>
        <rect x="168" y="270" width="4" height="78" rx="2" />
      </Region>

      {/* Calves */}
      <Region id="gastrocnemius_medial" {...props}>
        <ellipse cx="112" cy="408" rx="7" ry="30" />
      </Region>
      <Region id="gastrocnemius_medial" {...props}>
        <ellipse cx="128" cy="408" rx="7" ry="30" />
      </Region>
      <Region id="gastrocnemius_lateral" {...props}>
        <ellipse cx="96" cy="408" rx="7" ry="30" />
      </Region>
      <Region id="gastrocnemius_lateral" {...props}>
        <ellipse cx="144" cy="408" rx="7" ry="30" />
      </Region>
      <Region id="soleus" {...props}>
        <ellipse cx="104" cy="454" rx="10" ry="22" />
      </Region>
      <Region id="soleus" {...props}>
        <ellipse cx="136" cy="454" rx="10" ry="22" />
      </Region>
    </svg>
  );
}

export default function BodyDiagram({
  selected = [],
  aiFocus = [],
  primaryFocus = [],
  secondaryFocus = [],
  relatedFocus = [],
  heatmap = null,
  onChange,
  onMuscleClick,
  onMuscleLongPress,
  readOnly = false,
  defaultLayout = "compact",
  labelsOn: labelsOnProp,
  onLabelsToggle,
  hideInternalControls = false,
  // Optional subset of panels: ["head_front", "head_back", "front", "back"]
  regions = null,
}) {
  const [layout, setLayout] = React.useState(defaultLayout);
  const [hoveredId, setHoveredId] = React.useState(null);
  const [labelsOnLocal, setLabelsOnLocal] = React.useState(false);
  const labelsOn = labelsOnProp ?? labelsOnLocal;
  const setLabelsOn = onLabelsToggle ?? setLabelsOnLocal;

  const toggle = (id) => {
    if (readOnly) {
      onMuscleClick?.(id);
      return;
    }
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onChange?.(next);
    onMuscleClick?.(id);
  };

  const isLarge = layout === "large";
  const panelCls = isLarge ? "" : "max-w-[260px] mx-auto";
  const gridCls = isLarge ? "grid grid-cols-1 gap-6" : "grid grid-cols-2 gap-4 md:gap-6";

  const hoveredName = hoveredId ? MUSCLE_LABELS[hoveredId] || hoveredId : null;
  const figureProps = { selected, aiFocus, hoveredId, onToggle: toggle, onHover: setHoveredId, onLongPress: onMuscleLongPress, primaryFocus, secondaryFocus, relatedFocus, heatmap };
  const showRegion = (k) => !regions || regions.includes(k);

  return (
    <div className="space-y-6">
      {/* Layout + labels toggles (internal — hide when parent owns controls) */}
      {!readOnly && !hideInternalControls && (
        <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="diagram-controls">
          <label className="inline-flex items-center gap-2 text-xs text-ink-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={labelsOn}
              onChange={(e) => setLabelsOn(e.target.checked)}
              data-testid="toggle-labels"
              className="w-4 h-4 accent-sage-600"
            />
            <span className="label-overline">Show muscle name on hover</span>
          </label>
          <div className="flex items-center gap-2" data-testid="diagram-layout-toggle">
            <span className="label-overline">Layout:</span>
            <button
              type="button"
              onClick={() => setLayout("compact")}
              data-testid="layout-compact"
              className={`min-h-[36px] px-3 rounded-full text-xs transition-all ${!isLarge ? "bg-ink-900 text-white" : "bg-white border border-paper-200 text-ink-700 hover:bg-paper-100"}`}
            >
              Compact
            </button>
            <button
              type="button"
              onClick={() => setLayout("large")}
              data-testid="layout-large"
              className={`min-h-[36px] px-3 rounded-full text-xs transition-all ${isLarge ? "bg-ink-900 text-white" : "bg-white border border-paper-200 text-ink-700 hover:bg-paper-100"}`}
            >
              Large (easier to tap)
            </button>
          </div>
        </div>
      )}

      {/* Hovered-muscle banner */}
      {labelsOn && (
        <div
          className="rounded-full bg-ink-900 text-white text-sm px-4 py-2 inline-flex items-center gap-2 shadow-sm transition-opacity"
          data-testid="hovered-muscle-banner"
          style={{ opacity: hoveredName ? 1 : 0.5 }}
        >
          <span className="label-overline text-paper-200">Hovered</span>
          <span className="font-medium" data-testid="hovered-muscle-name">
            {hoveredName || "Hover or tap a muscle…"}
          </span>
        </div>
      )}

      {/* Head / Face detail panels */}
      {(showRegion("head_front") || showRegion("head_back")) && (
      <div className={regions ? "grid grid-cols-1 gap-6" : gridCls}>
        {showRegion("head_front") && (
        <div>
          <div className="label-overline mb-3 text-center">Head & Face — Anterior</div>
          <div className={`bg-paper-50 rounded-2xl p-2 sm:p-4 border border-paper-200 ${regions ? "max-w-md mx-auto" : isLarge ? "max-w-2xl mx-auto" : panelCls}`}>
            <HeadFront {...figureProps} />
          </div>
        </div>
        )}
        {showRegion("head_back") && (
        <div>
          <div className="label-overline mb-3 text-center">Head & Neck — Posterior</div>
          <div className={`bg-paper-50 rounded-2xl p-2 sm:p-4 border border-paper-200 ${regions ? "max-w-md mx-auto" : isLarge ? "max-w-2xl mx-auto" : panelCls}`}>
            <HeadBack {...figureProps} />
          </div>
        </div>
        )}
      </div>
      )}
      {/* Full body diagrams */}
      {(showRegion("front") || showRegion("back")) && (
      <div className={regions ? "grid grid-cols-1 gap-6" : gridCls}>
        {showRegion("front") && (
        <div>
          <div className="label-overline mb-3 text-center">Anterior — Full Body</div>
          <div className={`bg-paper-50 rounded-2xl p-2 sm:p-4 border border-paper-200 ${regions ? "max-w-lg mx-auto" : isLarge ? "max-w-3xl mx-auto" : ""}`}>
            <FrontFigure {...figureProps} />
          </div>
        </div>
        )}
        {showRegion("back") && (
        <div>
          <div className="label-overline mb-3 text-center">Posterior — Full Body</div>
          <div className={`bg-paper-50 rounded-2xl p-2 sm:p-4 border border-paper-200 ${regions ? "max-w-lg mx-auto" : isLarge ? "max-w-3xl mx-auto" : ""}`}>
            <BackFigure {...figureProps} />
          </div>
        </div>
        )}
      </div>
      )}
    </div>
  );
}
